













































































































































































































































import Component from 'vue-class-component'
import Vue from 'vue'
import CurrencyRatesService, {
  CurrencyRate,
  UsdPair,
} from '@/services/CurrencyRatesService'

const STALE_AFTER_DAYS = 5

const PAIRS_META: Array<{ code: string; flag: string }> = [
  { code: 'JPY', flag: '🇯🇵' },
  { code: 'EUR', flag: '🇪🇺' },
  { code: 'THB', flag: '🇹🇭' },
  { code: 'KRW', flag: '🇰🇷' },
]

// Алерт показываем один раз на загрузку приложения, а не при каждом
// возврате на страницу с сайдбаром.
let isStaleAlertShown = false

@Component
export default class CurrencyRatesPanel extends Vue {
  rates: CurrencyRate[] = []
  usdPairs: UsdPair[] = []
  isShowDialog = false
  isShowStaleAlert = false
  isLoading = false
  isPairsLoading = false
  filter = ''
  // «N ч назад» пересчитывается по таймеру, иначе в долгоживущей вкладке
  // подпись застынет на моменте загрузки
  now = Date.now()
  nowTimer = 0

  columns = [
    {
      name: 'currencyCode',
      label: 'Валюта',
      field: 'currencyCode',
      align: 'left',
      sortable: true,
    },
    {
      name: 'rate',
      label: 'За 1 USD',
      field: 'rate',
      align: 'right',
      sortable: true,
      format: (value: number) => value.toFixed(value >= 10 ? 2 : 4),
    },
    {
      name: 'date',
      label: 'Дата',
      field: 'date',
      align: 'right',
      format: (value: string) => this.formatDate(value),
    },
  ]

  // Выбранный вариант дизайна: «Карточка с флагами» + drawer справа.
  // Остальные варианты ('minimal' | 'dark' | 'glass') оставлены в шаблоне
  // и стилях неактивными — переключается здесь.
  get panelStyle(): string {
    return 'badges'
  }

  get isDrawerDialog(): boolean {
    return true
  }

  get isPairsUnavailable(): boolean {
    return !this.isPairsLoading && !this.usdPairs.length
  }

  get tableStyle(): string {
    return this.isDrawerDialog
      ? 'max-height: calc(100vh - 210px)'
      : 'max-height: 55vh'
  }

  get pairs(): Array<{ code: string; flag: string; value: string }> {
    // Прямые котировки пар с fx-api — без кросса через рубль.
    const byCode: Record<string, UsdPair> = {}

    this.usdPairs.forEach(pair => {
      byCode[pair.code] = pair
    })

    return PAIRS_META.filter(meta => byCode[meta.code]).map(meta => {
      const value = byCode[meta.code].value

      return {
        code: meta.code,
        flag: meta.flag,
        value: value.toFixed(value >= 10 ? 2 : 4),
      }
    })
  }

  // Все пары USD/<code> для полной таблицы в drawer
  get pairRows(): Array<{ currencyCode: string; rate: number; date: string }> {
    return [...this.usdPairs]
      .sort((a, b) => a.code.localeCompare(b.code))
      .map(pair => ({
        currencyCode: pair.code,
        rate: pair.value,
        date: pair.date,
      }))
  }

  get pairsLatestDate(): string | null {
    if (!this.usdPairs.length) {
      return null
    }

    return this.usdPairs
      .map(pair => pair.date)
      .reduce((max, date) => (date > max ? date : max))
  }

  get latestPairDate(): string | null {
    const dates = PAIRS_META.map(meta =>
      this.usdPairs.find(pair => pair.code === meta.code)
    )
      .filter(Boolean)
      .map(pair => (pair as UsdPair).date)

    if (!dates.length) {
      return null
    }

    return dates.reduce((max, date) => (date > max ? date : max))
  }

  get latestDate(): string | null {
    if (!this.rates.length) {
      return null
    }

    return this.rates
      .map(rate => rate.date)
      .reduce((max, date) => (date > max ? date : max))
  }

  get staleDays(): number {
    if (!this.latestDate) {
      return 0
    }

    const msInDay = 24 * 60 * 60 * 1000

    return Math.floor(
      (Date.now() - new Date(this.latestDate).getTime()) / msInDay
    )
  }

  get isStale(): boolean {
    // Пустой список курсов — тоже повод для тревоги
    return (
      !this.isLoading &&
      (!this.rates.length || this.staleDays > STALE_AFTER_DAYS)
    )
  }

  get staleMessage(): string {
    if (!this.rates.length) {
      return 'Курсы валют не загружены'
    }

    return `Курсы валют устарели: последнее обновление ${this.formatDate(
      this.latestDate as string
    )} (${this.staleDays} дн. назад)`
  }

  formatDate(date: string): string {
    // «8 июл» — короткий месяц без точки и без года
    return new Date(date)
      .toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
      .replace('.', '')
  }

  relativeTime(date: string): string {
    const minutes = Math.floor(
      (this.now - new Date(date).getTime()) / (60 * 1000)
    )

    if (minutes < 1) {
      return 'только что'
    }

    if (minutes < 60) {
      return `${minutes} мин назад`
    }

    const hours = Math.floor(minutes / 60)

    if (hours < 48) {
      return `${hours} ч назад`
    }

    return `${Math.floor(hours / 24)} дн назад`
  }

  async created() {
    this.nowTimer = window.setInterval(() => (this.now = Date.now()), 60 * 1000)

    await this.refresh()

    if (this.isStale && !isStaleAlertShown) {
      isStaleAlertShown = true
      this.isShowStaleAlert = true
    }
  }

  beforeDestroy() {
    window.clearInterval(this.nowTimer)
  }

  async refresh() {
    // Пары (панель и полная таблица) — напрямую с fx-api; рублёвые курсы
    // из БД посткалка грузим только для контроля протухания (ими считает бэкенд).
    this.isPairsLoading = true
    const pairsRequest = CurrencyRatesService.getUsdPairs()
      .then(pairs => (this.usdPairs = pairs))
      .catch(() => undefined)
      .finally(() => {
        this.isPairsLoading = false
        this.now = Date.now()
      })

    this.isLoading = true

    try {
      this.rates = await CurrencyRatesService.getRates()
    } catch (e) {
      // бэкенд недоступен — оставляем прежние значения
    } finally {
      this.isLoading = false
    }

    await pairsRequest
  }
}
